import { DateTime } from 'luxon';

import {
  CouponDurationTypeEnum,
  CouponFragment,
  Coupon__Subject,
  ErrorCodeEnum,
  ErrorFragment,
} from '@graphql/platform';

import { formatCurrency } from '@utils/formatters';

export const CHALLENGER_PROMO_GENERIC_ERROR =
  'Something went wrong applying this code. Try again.';

const INVALID_CODE = 'This code isn’t valid. Check it and try again.';
const EXPIRED_CODE = 'This code has expired.';
const ALREADY_USED = 'This code has already been used.';

/**
 * Order/appointment coupons discount pick-up fees (Figma 707:19326).
 * Subscription coupons discount storage plan / month (Figma 707:19398+).
 */
export const isAppointmentCoupon = (coupon: CouponFragment): boolean => {
  if (coupon.subject === Coupon__Subject.Order) return true;
  if (coupon.subject === Coupon__Subject.Subscription) return false;
  return /\bappointment\b/i.test(coupon.discountDescription ?? '');
};

export const isStorageCoupon = (coupon: CouponFragment): boolean => {
  if (coupon.subject === Coupon__Subject.Subscription) return true;
  if (coupon.subject === Coupon__Subject.Order) return false;
  return !isAppointmentCoupon(coupon);
};

/** Dollar amount off for the promo (flat amount, or % of the relevant base). */
export const couponDiscountAmount = (
  coupon: CouponFragment,
  baseAmount: number,
): number => {
  if (coupon.amount > 0) return coupon.amount;
  if (coupon.percent > 0) return baseAmount * coupon.percent;
  return 0;
};

const couponDurationMonths = (coupon: CouponFragment): number | null => {
  if (isAppointmentCoupon(coupon)) return null;

  switch (coupon.duration) {
    case CouponDurationTypeEnum.Once:
      return 1;
    case CouponDurationTypeEnum.Repeating:
      return coupon.durationInMonths ?? null;
    case CouponDurationTypeEnum.Forever:
      return null;
    default:
      return null;
  }
};

/**
 * Success caption under the promo field.
 * Appointment: Figma 707:19209 — "off your appointment."
 * Storage: Figma 707:19398 / 19587 / 19776.
 */
export const challengerPromoSuccessMessage = (
  coupon: CouponFragment,
  baseAmount: number,
): string => {
  const amountLabel = formatCurrency(couponDiscountAmount(coupon, baseAmount));

  if (isAppointmentCoupon(coupon)) {
    return `Promo applied. ${amountLabel} off your appointment.`;
  }

  switch (coupon.duration) {
    case CouponDurationTypeEnum.Once:
      return `Promo applied. ${amountLabel} off your first month of storage.`;
    case CouponDurationTypeEnum.Repeating: {
      const months = coupon.durationInMonths;
      const monthsLabel =
        months != null
          ? `${months} ${months === 1 ? 'month' : 'months'}`
          : 'months';
      return `Promo applied. ${amountLabel} off your storage for the next ${monthsLabel}.`;
    }
    case CouponDurationTypeEnum.Forever:
      return `Promo applied. ${amountLabel} off your storage every month.`;
    default:
      return `Promo applied. ${amountLabel} off your storage.`;
  }
};

/** Booking summary discount line label, e.g. `"SAVE20" applied` or `"FIRST15" applied (1 mo)`. */
export const challengerPromoSummaryLabel = (coupon: CouponFragment): string => {
  const months = couponDurationMonths(coupon);
  if (months != null) {
    return `“${coupon.promoCode}” applied (${months} mo)`;
  }
  return `“${coupon.promoCode}” applied`;
};

export const challengerPromoPickupSubtext = (
  pickupDayLabel: string | undefined,
): string | undefined => {
  if (!pickupDayLabel) return undefined;
  return `${pickupDayLabel} · promo applied`;
};

export const challengerPromoMonthlySubtext = (
  coupon: CouponFragment,
  fullMonthlyAmount: number,
  monthStartLabel: string | undefined,
): string | undefined => {
  if (isAppointmentCoupon(coupon)) {
    return monthStartLabel;
  }

  switch (coupon.duration) {
    case CouponDurationTypeEnum.Once:
      return monthStartLabel;
    case CouponDurationTypeEnum.Repeating: {
      const months = coupon.durationInMonths;
      if (months == null) {
        return `Promo applied, then ${formatCurrency(fullMonthlyAmount)}`;
      }
      return `Promo applied for first ${months} months, then ${formatCurrency(
        fullMonthlyAmount,
      )}`;
    }
    case CouponDurationTypeEnum.Forever:
      return 'Promo applied every month';
    default:
      return monthStartLabel;
  }
};

/** Map GraphQL coupon errors to challenger handoff copy. */
export const challengerPromoErrorMessage = (
  error: ErrorFragment | undefined,
  options?: {
    showCutoffError?: boolean;
    scheduledCutoff?: string | null;
  },
): string | undefined => {
  if (options?.showCutoffError && options.scheduledCutoff) {
    const date = DateTime.fromISO(options.scheduledCutoff);
    const formatted = date.isValid
      ? date.toFormat('MMMM d, yyyy')
      : options.scheduledCutoff;
    return `This code is only valid for appointments booked by ${formatted}.`;
  }

  if (!error) return undefined;

  switch (error.errorCode) {
    case ErrorCodeEnum.CouponNotFound:
      return INVALID_CODE;
    case ErrorCodeEnum.PromoCodeNotRedeemable:
      return ALREADY_USED;
    case ErrorCodeEnum.CouponNotEligible: {
      const message = error.errorMessage?.trim();
      if (message?.toLowerCase().includes('expired')) return EXPIRED_CODE;
      if (message) return message;
      return INVALID_CODE;
    }
    case ErrorCodeEnum.ExistingCoupon:
      return ALREADY_USED;
    default:
      return CHALLENGER_PROMO_GENERIC_ERROR;
  }
};
